// Кибер-фон с сеткой и переливами (без снеговика — он теперь глобально в Layout)
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

export default function CyberBackground({
  theme = 'purple',
  className = 'fixed inset-0 z-[0] pointer-events-none',
  gridSize = 120,
  subGridRatio = 4,
  gridLine = 1,
  subGridLine = 1,
}) {
  const { pathname } = useLocation()
  const isChannelPage = /(?:^|\/)channel(?:\/|$)/i.test(pathname)

  const [bodyFlags, setBodyFlags] = useState({ noGrid: false, noAll: false, noAnim: false })

  // следим за классами на body
  useEffect(() => {
    let prev = { noGrid: false, noAll: false, noAnim: false }
    const update = () => {
      const cls = document.body.classList
      const next = {
        noGrid: cls.contains('no-cyber-grid'),
        noAll: cls.contains('no-cyber-bg'),
        noAnim: cls.contains('no-cyber-anim'),
      }
      if (next.noGrid !== prev.noGrid || next.noAll !== prev.noAll || next.noAnim !== prev.noAnim) {
        prev = next
        setBodyFlags(next)
      }
    }
    update()
    const mo = new MutationObserver(update)
    mo.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => mo.disconnect()
  }, [])

  const palette = useMemo(() => {
    switch (theme) {
      case 'blue':
        return { glow1: 'rgba(99,102,241,.22)', glow2: 'rgba(56,189,248,.20)', prismA: 'rgba(56,189,248,.22)', prismB: 'rgba(99,102,241,.22)' }
      case 'green':
        return { glow1: 'rgba(34,197,94,.22)', glow2: 'rgba(16,185,129,.20)', prismA: 'rgba(16,185,129,.22)', prismB: 'rgba(34,197,94,.22)' }
      default:
        return { glow1: 'rgba(168,85,247,.25)', glow2: 'rgba(139,92,246,.22)', prismA: 'rgba(236,72,153,.22)', prismB: 'rgba(139,92,246,.22)' }
    }
  }, [theme])

  const hostRef = useRef(null)

  if (bodyFlags.noAll) return null

  // фоновые параметры
  const MAIN_ALPHA = 0.30, SUB_ALPHA = 0.10
  const mainCell = Math.max(4, Math.round(gridSize))
  const subCell = Math.max(2, Math.round(mainCell / Math.max(1, subGridRatio)))
  const mainLine = Math.max(1, Math.round(gridLine))
  const subLine = Math.max(1, Math.round(subGridLine))
  const prismMask = 'radial-gradient(70% 60% at 50% 38%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.0) 80%)'
  const allowSheen = !bodyFlags.noAnim && !isChannelPage

  return (
    <div ref={hostRef} className={className} id="__cyber_host__">
      {/* подложка */}
      <div className="absolute inset-0 bg-slate-950" />
      {/* ореолы */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{
          backgroundImage: [
            `radial-gradient(1200px 560px at 50% -8%, ${palette.glow1}, transparent 70%)`,
            `radial-gradient(900px 420px at 85% 110%, ${palette.glow2}, transparent 70%)`,
          ].join(','),
        }}
      />
      {/* сетка */}
      {!bodyFlags.noGrid && (
        <div
          className="absolute inset-0 will-change-transform"
          style={{
            backgroundImage: [
              `linear-gradient(rgba(255,255,255,${MAIN_ALPHA}) ${mainLine}px, transparent ${mainLine}px)`,
              `linear-gradient(90deg, rgba(255,255,255,${MAIN_ALPHA}) ${mainLine}px, transparent ${mainLine}px)`,
              `linear-gradient(rgba(255,255,255,${SUB_ALPHA}) ${subLine}px, transparent ${subLine}px)`,
              `linear-gradient(90deg, rgba(255,255,255,${SUB_ALPHA}) ${subLine}px, transparent ${subLine}px)`,
            ].join(','),
            backgroundSize: `${mainCell}px ${mainCell}px, ${mainCell}px ${mainCell}px, ${subCell}px ${subCell}px, ${subCell}px ${subCell}px`,
          }}
        />
      )}
      {/* переливы — ВЫКЛ на channel/* */}
      {allowSheen && (
        <>
          <div
            className="absolute -inset-[6%] mix-blend-overlay pointer-events-none animate-[spin_48s_linear_infinite]"
            style={{
              backgroundImage:
                `conic-gradient(from 0deg at 50% 50%,
                  rgba(255,0,140,0.18),
                  ${palette.prismA},
                  rgba(255,212,0,0.18),
                  ${palette.prismB},
                  rgba(0,225,255,0.18),
                  rgba(255,0,140,0.18)
                )`,
              filter: 'saturate(120%) brightness(110%)',
              maskImage: prismMask,
              WebkitMaskImage: prismMask,
              opacity: 0.35,
              willChange: 'transform, opacity',
            }}
          />
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute -inset-x-[40%] -inset-y-[20%] animate-[slide_16s_linear_infinite]"
              style={{
                backgroundImage:
                  'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.0) 65%, transparent 100%)',
                opacity: 0.45,
                willChange: 'transform, opacity',
              }}
            />
          </div>
        </>
      )}

      {/* нижний градиент */}
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/70 to-transparent" />
    </div>
  )
}

/* keyframes (инлайн, один раз) */
const styleEl = typeof document !== 'undefined' ? document.createElement('style') : null
if (styleEl && !document.getElementById('cyber-bg-kf')) {
  styleEl.id = 'cyber-bg-kf'
  styleEl.textContent = `
    @keyframes slide { from { transform: translateX(-10%); } to { transform: translateX(10%); } }
    @keyframes spin  { to   { transform: rotate(360deg); } }
  `
  document.head.appendChild(styleEl)
}
